/** Mon–Fri (local calendar). */
import { TOTAL_MONTH_HOURS } from './constants.js';
import { localMonthKey } from './dates.js';

export function isWeekday(year: number, month: number, day: number): boolean {
  const dow = new Date(year, month, day).getDay();
  return dow >= 1 && dow <= 5;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function countWorkdaysInMonth(year: number, month: number): number {
  const last = daysInMonth(year, month);
  let n = 0;
  for (let d = 1; d <= last; d += 1) {
    if (isWeekday(year, month, d)) n += 1;
  }
  return n;
}

export function countWorkdaysThrough(year: number, month: number, throughDay: number): number {
  let n = 0;
  for (let d = 1; d <= throughDay; d += 1) {
    if (isWeekday(year, month, d)) n += 1;
  }
  return n;
}

/** Ideal break-even tempo % after `throughDay` (workday-linear month). */
export function workdayIdealPct(year: number, month: number, throughDay: number): number {
  const total = countWorkdaysInMonth(year, month);
  if (total <= 0) return 0;
  const elapsed = countWorkdaysThrough(year, month, throughDay);
  return Math.round((elapsed / total) * 10000) / 100;
}

/** Share of month workdays elapsed (0..1), Mon–Fri only. */
export function workdayMonthProgress(now: Date = new Date()): number {
  const year = now.getFullYear();
  const month = now.getMonth();
  const total = countWorkdaysInMonth(year, month);
  if (total <= 0) return 1;
  const elapsed = countWorkdaysThrough(year, month, now.getDate());
  return Math.min(1, Math.max(0, elapsed / total));
}

/** Hours expected by `now` = the fixed monthly budget × the share of workdays elapsed. */
export function expectedMonthHoursSoFar(
  now: Date = new Date(),
  monthBudget: number = TOTAL_MONTH_HOURS,
): number {
  return monthBudget * workdayMonthProgress(now);
}

/** Workday-clock progress of an arbitrary YYYY-MM relative to `now`: 1 for past, 0 for future. */
export function workdayMonthProgressFor(monthKey: string, now: Date = new Date()): number {
  const currentMonthKey = localMonthKey(now);
  if (monthKey < currentMonthKey) return 1;
  if (monthKey > currentMonthKey) return 0;
  return workdayMonthProgress(now);
}

/** Mon–Fri days remaining in `now`'s month, counting today when it is a workday. */
export function workdaysRemainingInMonth(now: Date = new Date()): number {
  const year = now.getFullYear();
  const month = now.getMonth();
  const lastDay = daysInMonth(year, month);
  const today = now.getDate();
  let n = 0;
  for (let d = today; d <= lastDay; d += 1) {
    if (isWeekday(year, month, d)) n += 1;
  }
  return n;
}
